import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Heart, Users, Search, Shield, Phone,
  CheckCircle, ArrowRight, Building2, Globe, Loader2
} from 'lucide-react';
import { facilityApi } from '../utils/api';

const AboutPage = () => {
  const [siteStats, setSiteStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await facilityApi.getStats();
        setSiteStats(response);
      } catch (err) {
        console.error('Error fetching site stats:', err);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex p-4 rounded-full bg-white/10 backdrop-blur-sm mb-6">
              <Heart className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              About Addiction Rehab Call
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              We're a trusted directory service connecting individuals and families 
              with quality addiction treatment centers across the United States.
            </p>
            
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-4">
              <Phone className="h-5 w-5 mr-3" />
              <span className="mr-2">24/7 Confidential Help:</span>
              <a 
                href="tel:1-888-968-1014"
                className="text-xl font-bold hover:underline"
              >
                1-888-968-1014
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                To make addiction treatment accessible by connecting people in need 
                with quality treatment facilities nationwide.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  What We Do
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Directory Service</h4>
                      <p className="text-gray-600">
                        We maintain a comprehensive directory of addiction treatment facilities 
                        across all 50 states.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Referral Services</h4>
                      <p className="text-gray-600">
                        We help match individuals with treatment centers that meet 
                        their specific needs and circumstances.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Information Resource</h4>
                      <p className="text-gray-600">
                        We provide educational content about addiction, treatment options, 
                        and recovery resources.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">24/7 Support</h4>
                      <p className="text-gray-600">
                        Our helpline is available around the clock to provide 
                        immediate assistance and support.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-blue-900 mb-4">
                  Important Disclaimer
                </h3>
                <div className="space-y-3 text-blue-800">
                  <p className="flex items-start">
                    <Shield className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                    We are a directory and referral service, not a treatment provider
                  </p>
                  <p className="flex items-start">
                    <Shield className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                    We do not provide medical treatment, therapy, or counseling services
                  </p>
                  <p className="flex items-start">
                    <Shield className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                    We may receive compensation from facilities for successful referrals
                  </p>
                  <p className="flex items-start">
                    <Shield className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                    All treatment decisions should be made with qualified healthcare professionals
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How Our Service Works
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Getting help for addiction shouldn't be complicated. Here's how we make it simple.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center border-0 shadow-lg">
                <CardContent className="pt-8 pb-6">
                  <div className="inline-flex p-4 rounded-full bg-blue-100 text-blue-600 mb-4">
                    <Search className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">1. Search & Find</h3>
                  <p className="text-gray-600 mb-4">
                    Use our directory to search for treatment centers by location, 
                    treatment type, or specialization.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="pt-8 pb-6">
                  <div className="inline-flex p-4 rounded-full bg-purple-100 text-purple-600 mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">2. Connect & Consult</h3>
                  <p className="text-gray-600 mb-4">
                    Contact facilities directly or call our helpline for 
                    personalized assistance and recommendations.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="pt-8 pb-6">
                  <div className="inline-flex p-4 rounded-full bg-green-100 text-green-600 mb-4">
                    <Heart className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">3. Start Treatment</h3>
                  <p className="text-gray-600 mb-4">
                    Begin your recovery journey with the treatment center 
                    that best fits your needs and circumstances.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Impact
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We're proud to help connect thousands of people with the treatment they need.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {siteStats ? `${siteStats.total_facilities.toLocaleString()}+` : (
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                  )}
                </div>
                <div className="text-gray-600">Treatment Centers</div>
                <div className="text-sm text-gray-500 mt-1">Nationwide Network</div>
              </div>

              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">
                  {siteStats ? Object.keys(siteStats.facilities_by_state).length : (
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                  )}
                </div>
                <div className="text-gray-600">States Covered</div>
                <div className="text-sm text-gray-500 mt-1">Coast to Coast</div>
              </div>

              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">24/7</div>
                <div className="text-gray-600">Support Available</div>
                <div className="text-sm text-gray-500 mt-1">Always Here to Help</div>
              </div>

              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">Free</div>
                <div className="text-gray-600">Service Cost</div>
                <div className="text-sm text-gray-500 mt-1">No Charge to Users</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Facilities */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex p-4 rounded-full bg-blue-100 text-blue-600 mb-6">
              <Building2 className="h-8 w-8" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              For Treatment Facilities
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              Are you a treatment facility looking to connect with people seeking help? 
              Join our directory and expand your reach.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <Globe className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Increased Visibility</h3>
                <p className="text-sm text-gray-600">Reach people actively seeking treatment in your area</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <Users className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Quality Referrals</h3>
                <p className="text-sm text-gray-600">Connect with motivated individuals ready for treatment</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <Shield className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Professional Review</h3>
                <p className="text-sm text-gray-600">All listings are reviewed to ensure quality standards</p>
              </div>
            </div>

            <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600">
              <Link to="/facility-signup">
                <Building2 className="h-5 w-5 mr-2" />
                List Your Facility
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Questions About Our Service?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            We're here to help you understand how our directory works and 
            how we can assist in finding the right treatment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/contact">
                Contact Us
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
              <a href="tel:1-888-968-1014">
                <Phone className="h-5 w-5 mr-2" />
                Call: 1-888-968-1014
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;